import type { operations } from '@molchanovs/api-contract';
import { z } from 'zod';

/** The CRM Program Log collection is intentionally fixed to the five-row CRM table pattern. */
export const crmBtProgramLogPageSize = 5 as const;

/**
 * Product-default Flow option. This is the exact current `dbValue` published by the canonical
 * `bt_program_flow_type` catalogue; it is not a presentation-case conversion.
 */
export const crmBtProgramLogDefaultFlowDbValue = 'Standard' as const;

export const crmBtProgramLogDeleteResponseSchema = z.object({
  data: z.object({
    operationId: z.uuid(),
    status: z.literal('accepted'),
    monitorUrl: z.string().startsWith('/api/v1/operations/'),
  }),
});
export type CrmBtProgramLogDeleteResponse = z.infer<typeof crmBtProgramLogDeleteResponseSchema>;

type BtProgramLogDetailOperation = operations['getBtProgramLog'];
type PatchBtProgramLogOperation = operations['patchBtProgramLog'];
type CreateBtProgramLogOperation = operations['postApiV1BtProgramLogs'];
type BtProgramLogListOperation = operations['getUserProfileBtProgramLogs'];
type BtProgramLogStatusesOperation = operations['getApiV1BtProgramLogStatuses'];
type BtLevelsOperation = operations['getApiV1BtLevels'];
type BtProgramLogFlowsOperation = operations['getApiV1BtProgramLogFlows'];
type TrainingProgramsOperation = operations['getApiV1TrainingPrograms'];
type TrainingProgramDetailOperation = operations['getApiV1TrainingProgramsByTrainingProgramId'];

export type CrmBtProgramLogDetailResponse =
  BtProgramLogDetailOperation['responses'][200]['content']['application/json'];
export type CrmBtProgramLogPatchResponse =
  PatchBtProgramLogOperation['responses'][200]['content']['application/json'];
export type CrmBtProgramLogPatch =
  PatchBtProgramLogOperation['requestBody']['content']['application/merge-patch+json'];
export type CrmBtProgramLogCreate =
  CreateBtProgramLogOperation['requestBody']['content']['application/json'];
export type CrmBtProgramLogCreateResponse =
  CreateBtProgramLogOperation['responses'][201]['content']['application/json'];
export type CrmBtProgramLogListResponse =
  BtProgramLogListOperation['responses'][200]['content']['application/json'];
export type CrmBtProgramLogStatusesResponse =
  BtProgramLogStatusesOperation['responses'][200]['content']['application/json'];
export type CrmBtLevelsResponse =
  BtLevelsOperation['responses'][200]['content']['application/json'];
export type CrmBtProgramLogFlowsResponse =
  BtProgramLogFlowsOperation['responses'][200]['content']['application/json'];
type TrainingProgramsContractResponse =
  TrainingProgramsOperation['responses'][200]['content']['application/json'];
export type CrmBtProgramLogTrainingProgramDetailResponse =
  TrainingProgramDetailOperation['responses'][200]['content']['application/json'];
export type CrmBtProgramLogTrainingProgramsResponse = Omit<
  TrainingProgramsContractResponse,
  'data'
> & {
  data: {
    training_programs: Omit<
      TrainingProgramsContractResponse['data']['training_programs'][number],
      | 'training_program_id'
      | 'available'
      | 'type'
      | 'image'
      | 'thumbnail'
      | 'media'
      | 'videoUrl'
      | 'videoSource'
    >[];
  };
};
/** Local selector view model; the API page metadata is consumed during its bounded traversal. */
export type CrmBtProgramLogTrainingProgramOptionsResponse = Pick<
  CrmBtProgramLogTrainingProgramsResponse,
  'data'
>;

const coverSchema = z.object({
  source: z.enum(['tigris', 'db_source', 'missing']),
  url: z.url().nullable(),
});

const statusSchema = z.object({
  label: z.string().nullable(),
  optionId: z.uuid().nullable(),
});

export const crmBtProgramLogListResponseSchema = z.object({
  data: z.object({
    items: z.array(
      z.object({
        id: z.uuid(),
        program: z
          .object({
            cover: coverSchema,
            displayName: z.string().nullable(),
            id: z.uuid(),
          })
          .nullable(),
        startedAt: z.iso.datetime().nullable(),
        status: statusSchema,
        workoutsCompleted: z.number().int().nonnegative(),
      })
    ),
  }),
  meta: z.object({
    hasNextPage: z.boolean(),
    limit: z.number().int().positive(),
    nextCursor: z.string().nullable(),
    totalItems: z.number().int().nonnegative(),
  }),
});

export const crmBtProgramLogDetailResponseSchema = z.object({
  data: z.object({
    amountPaid: z
      .object({
        amount: z.number(),
        currency: z.string().nullable(),
      })
      .nullable(),
    fieldVisibility: z.object({
      amountPaid: z.enum(['visible', 'redacted', 'unavailable']),
      participant: z.enum(['visible', 'redacted', 'unavailable']),
    }),
    id: z.uuid(),
    trainingProgramId: z.uuid().nullable(),
    participant: z
      .object({
        displayName: z.string().nullable(),
        profileRef: z.uuid(),
      })
      .nullable(),
    program: z
      .object({
        btLevels: z.array(
          z.object({
            label: z.string().nullable(),
            optionId: z.uuid().nullable(),
          })
        ),
        cover: coverSchema,
        dimensions: z.object({
          access: z.string().nullable(),
          directory: z.string().nullable(),
          format: z.string().nullable(),
          participation: z.string().nullable(),
        }),
        displayName: z.string().nullable(),
        id: z.uuid(),
        instructors: z.array(
          z.object({
            displayName: z.string().nullable(),
            id: z.uuid(),
          })
        ),
        memberPrice: z.object({
          amount: z.number().nullable(),
          currency: z.literal('USD'),
        }),
      })
      .nullable(),
    startedAt: z.iso.datetime().nullable(),
    status: statusSchema,
    workouts: z.object({
      completed: z.number().int().nonnegative(),
      lastCompletedAt: z.iso.datetime().nullable(),
      skipped: z.number().int().nonnegative(),
      total: z.number().int().nonnegative(),
    }),
  }),
});

const mirrorCodeSchema = z
  .enum([
    'PROVIDER_REJECTED',
    'OUTCOME_UNKNOWN',
    'IDENTITY_PENDING',
    'PREDECESSOR_PENDING',
    'DELIVERY_UNAVAILABLE',
  ])
  .nullable();

export const crmBtProgramLogCreateResponseSchema: z.ZodType<CrmBtProgramLogCreateResponse> =
  crmBtProgramLogDetailResponseSchema.extend({
    meta: z.object({
      localCommitted: z.literal(true),
      operationId: z.uuid(),
      identityBound: z.boolean(),
      mirror: z.object({
        state: z.enum(['confirmed', 'failed', 'unknown', 'dependency_pending']),
        code: mirrorCodeSchema,
        effects: z
          .array(
            z.object({
              effect: z.enum(['programLog', 'userProgramRelations']),
              state: z.enum(['confirmed', 'failed', 'unknown', 'dependency_pending']),
              code: mirrorCodeSchema,
            })
          )
          .max(2),
      }),
    }),
  });

export const crmBtProgramLogPatchResponseSchema: z.ZodType<CrmBtProgramLogPatchResponse> =
  crmBtProgramLogDetailResponseSchema.extend({
    meta: z.object({
      localCommitted: z.literal(true),
      operationId: z.uuid(),
      identityBound: z.null(),
      mirror: z.object({
        state: z.enum(['confirmed', 'failed', 'unknown', 'dependency_pending']),
        code: mirrorCodeSchema,
        effects: z
          .array(
            z.object({
              effect: z.literal('programLog'),
              state: z.enum(['confirmed', 'failed', 'unknown', 'dependency_pending']),
              code: mirrorCodeSchema,
            })
          )
          .length(1),
      }),
    }),
  });

export const crmBtProgramLogStatusesResponseSchema = z.object({
  data: z.object({
    programLogStatuses: z.array(
      z.object({
        dbValue: z.string().min(1),
        display: z.string().min(1),
        id: z.uuid(),
      })
    ),
  }),
});

export const crmBtLevelsResponseSchema: z.ZodType<CrmBtLevelsResponse> = z.object({
  data: z.object({
    btLevels: z.array(z.object({ id: z.uuid(), label: z.string().min(1) })),
  }),
});

export const crmBtProgramLogFlowsResponseSchema = z.object({
  data: z.object({
    programLogFlows: z.array(
      z.object({
        dbValue: z.string().min(1),
        display: z.string().min(1),
        id: z.uuid(),
      })
    ),
  }),
});

/**
 * Existing BT Program catalogue response used only as an independent filter selector.
 * The Program Log table itself never treats this catalogue as a substitute for log rows.
 */
export const crmBtProgramLogTrainingProgramsPageSchema = z.object({
  data: z.object({
    training_programs: z.array(
      z.object({
        cover: z.string().nullable(),
        status: z.string().nullable(),
        title: z.string(),
        description: z.string().nullable(),
        categories: z.array(z.string()),
        typeLevel: z.enum(['free', 'premium']).nullable(),
        programType: z.enum(['breathing', 'freediving']).nullable(),
        instructors: z.array(
          z.object({
            id: z.uuid(),
            displayName: z.string().min(1),
            avatarUrl: z.url().nullable(),
            profileType: z.literal('instructor'),
          })
        ),
        memberPriceUsdMobile: z.number().nullable(),
        nonMemberPriceUsdMobile: z.number().nullable(),
        videoCover: z.string().nullable(),
        freePreview: z.object({ moduleId: z.uuid(), slideId: z.uuid() }).nullable(),
        trainingProgramId: z.uuid(),
        revenueCatProductId: z.string().min(1).max(255).nullable().default(null),
      })
    ),
  }),
  meta: z.object({
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    totalItems: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  }),
}) satisfies z.ZodType<CrmBtProgramLogTrainingProgramsResponse>;

export const crmBtProgramLogTrainingProgramDetailSchema = z.object({
  data: z.object({
    training_program:
      crmBtProgramLogTrainingProgramsPageSchema.shape.data.shape.training_programs.element.extend({
        training_program_id: z.uuid(),
        available: z.boolean(),
        type: z.string().nullable(),
        image: z.string().nullable(),
        thumbnail: z.string().nullable(),
        media: z.object({ type: z.literal('image'), url: z.string() }).nullable(),
        videoUrl: z.url().nullable(),
        videoSource: z.enum(['file', 'external']).nullable(),
        additionalProgramsData: z.array(
          z.object({
            trainingProgramId: z.uuid(),
            revenueCatProductId: z.string().min(1).max(255).nullable().default(null),
            title: z.string().nullable(),
            memberPriceUsdMobile: z.number().nullable(),
            nonMemberPriceUsdMobile: z.number().nullable(),
          })
        ),
        instructions: z.array(
          z.object({
            instructionId: z.uuid(),
            index: z.number().nullable(),
            moduleId: z.uuid(),
            slides: z.array(
              z.object({
                cover: z
                  .object({
                    url: z.url().regex(/^https:\/\//u),
                    expires_at: z.iso.datetime().nullable(),
                  })
                  .nullable()
                  .default(null),
                id: z.uuid(),
                index: z.number().nullable(),
                title: z.string().nullable(),
                type: z.string().nullable(),
              })
            ),
            title: z.string().nullable(),
            type: z.string().nullable(),
          })
        ),
        memberPrice: z.object({ amount: z.number().nullable(), currency: z.literal('USD') }),
        dimensions: z.object({
          participation: z.string().nullable(),
          access: z.string().nullable(),
          format: z.string().nullable(),
          directory: z.string().nullable(),
        }),
        btLevels: z.array(z.object({ optionId: z.uuid(), label: z.string().nullable() })),
        instructors: z.array(
          z.object({
            id: z.uuid(),
            displayName: z.string().min(1),
            avatarUrl: z.url().nullable(),
            profileType: z.literal('instructor'),
          })
        ),
      }),
  }),
});

export type CrmBtProgramLogFilters = {
  /** Empty explicitly requests All; undefined lets the route restore its Standard default. */
  flows?: string[];
  programIds?: string[];
  q?: string;
  startedFrom?: string;
  startedTo?: string;
  statuses?: string[];
};

export type CrmBtProgramLogListItem = CrmBtProgramLogListResponse['data']['items'][number];
export type CrmBtProgramLogDetail = CrmBtProgramLogDetailResponse['data'];
export type CrmBtProgramLogFieldVisibility = CrmBtProgramLogDetail['fieldVisibility'];
export type CrmBtProgramLogTrainingProgram =
  CrmBtProgramLogTrainingProgramsResponse['data']['training_programs'][number];
